import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Spinner } from "@/components/ui/spinner";

import { AppHeader } from "../features/chat";
import SettingsPanel from "../shared/components/SettingsPanel";
// Projects component removed
import { ChatPanel, useChatState } from "../features/chat";
import { ChatBlockedState } from "../features/chat/components/molecules/ChatBlockedState";
import { DataPanel } from "../features/documents";
import { KnowledgeBasePanel } from "../features/knowledgebase";
import { BlocksPanel } from "../features/blocks";
import { IntegrationsPanel } from "../features/integrations";
import { ProjectVariablesPanel } from "../features/projectvariables";
import { AuthFlow } from "../features/auth";
// import DocumentEditor from "../features/editor";
import { ExecutionChangeModal, ExecutionTemplatesPanel } from "../features/projects";
import LogoutConfirmationModal from "../shared/components/common/LogoutConfirmationModal";
import SaveConflictModal from "../shared/components/common/SaveConflictModal";
import {
  SaveConflictUserResolutionError,
  StorageService,
  type SaveConflictChoice,
  type SaveConflictInfo,
} from "../shared/services/storageService";
import { WarningModal } from "../shared/components/common/WarningModal";
import { NavigationTab } from "../types/navigationTypes";
import { ExtractedDataField } from "../types/dataTypes";
import { cn } from "@/lib/utils";
import { configService } from "../shared/services/configService";
import { ExecutionService } from "../features/projects";
import { authService } from "../features/auth/services/authService";
import { authenticateFromUrlToken } from "../core/lib/supabase";

interface MainApplicationProps {
  // Props for production configuration - if not provided, use development values
  workpillId?: string;
  executionId?: string;
  userId?: string;
  isDevelopment?: boolean;
}

const MainApplication: React.FC<MainApplicationProps> = ({
  workpillId,
  executionId,
  userId,
  isDevelopment = true // Default to development mode
}) => {
  const [activeTab, setActiveTab] = useState<NavigationTab>('chat');
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | undefined>(executionId);
  
  // Project Selector State
  const [isProjectSelectorOpen, setIsProjectSelectorOpen] = useState(false);
  
  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Settings states
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // States for execution change confirmation modal
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [pendingExecution, setPendingExecution] = useState<{
    executionId: string;
    workpillId: string | null;
    executionName?: string;
  } | null>(null);
  const [currentExecutionName, setCurrentExecutionName] = useState<string | undefined>(undefined);
  const [isChangingExecution, setIsChangingExecution] = useState(false);

  // States for interruption warning modal
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningType, setWarningType] = useState<'execution_change' | 'logout' | null>(null);
  const [pendingWarningAction, setPendingWarningAction] = useState<() => void | Promise<void>>(() => {});

  // States for logout confirmation
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const saveConflictResolverRef = useRef<((choice: SaveConflictChoice) => void) | null>(null);
  const [saveConflictPrompt, setSaveConflictPrompt] = useState<SaveConflictInfo | null>(null);

  // Initialize global configuration when mounting main component
  useEffect(() => {
    const config = {
      workpillId,
      executionId,
      userId,
      isDevelopment
    };
    
    // Only add non-undefined values
    const configToInitialize: any = {};
    if (workpillId) configToInitialize.workpillId = workpillId;
    if (executionId) configToInitialize.executionId = executionId;
    if (userId) configToInitialize.userId = userId;
    if (isDevelopment !== undefined) configToInitialize.isDevelopment = isDevelopment;
    
    configService.initialize(configToInitialize);
  }, [workpillId, executionId, userId, isDevelopment]);

  useEffect(() => {
    StorageService.setSaveConflictHandler(async (info) => {
      return new Promise((resolve) => {
        saveConflictResolverRef.current = resolve;
        setSaveConflictPrompt(info);
      });
    });
    return () => {
      StorageService.setSaveConflictHandler(null);
      saveConflictResolverRef.current = null;
    };
  }, []);

  // Get current configuration
  const appConfig = configService.getConfig();

  // 🧠 LIFTED STATE: Chat state management
  // Move chat state here so it persists between tab changes
  const chatState = useChatState(appConfig.workpillId, selectedExecutionId || appConfig.executionId);

  // Check authentication when mounting component
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initAuth = async () => {
      try {
        // First, check if there are OAuth tokens in the URL (Google callback)
        const hasUrlTokens = await authenticateFromUrlToken();
        
        if (hasUrlTokens) {
          console.log('✅ OAuth tokens processed from URL');
          // Clean URL of OAuth parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Check if there's already an active session
        const session = await authService.getSession();
        
        if (session?.user) {
          console.log('✅ Existing session found:', session.user.email);
          setIsAuthenticated(true);
          setCurrentUser(session.user);
          configService.setUserId(session.user.id);
        } else {
          console.log('ℹ️ No active session, showing login');
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        setAuthLoading(false);
      }

      // Subscribe to authentication changes
      unsubscribe = authService.subscribe((state) => {
        if (state.user) {
          setIsAuthenticated(true);
          setCurrentUser(state.user);
          configService.setUserId(state.user.id);
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
          configService.clearAuth();
        }
      });
    };

    initAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // ... (rest of initialization useEffects) ...
  useEffect(() => {
    if (!isAuthenticated) return;
    
    configService.logCurrentConfig();
    
    if (executionId) {
      loadCurrentExecutionName(executionId);
    }
  }, [workpillId, executionId, userId, isDevelopment, isAuthenticated]);

  // Load current execution name
  const loadCurrentExecutionName = async (execId: string) => {
    try {
      const summary = await ExecutionService.getExecutionSummary(execId);
      if (summary) {
        setCurrentExecutionName(summary.execution.name);
      }
    } catch (error) {
      console.error('Error loading current execution name:', error);
    }
  };

  // Start auto-save when add-in loads
  useEffect(() => {
    console.log('🔄 Starting auto-save system...');
    StorageService.startAutoSave(5); // Auto-save every 5 minutes
    
    return () => {
      console.log('🛑 Stopping auto-save system...');
      StorageService.stopAutoSave();
    };
  }, []);

  const handleTabChange = (tab: NavigationTab) => {
    setActiveTab(tab);
  };

  const handleNavigateToChat = () => {
    setActiveTab('chat');
  };

  const handleNavigateToDocuments = () => {
    setActiveTab('documents');
  };

  const handleNavigateToKnowledgeBase = () => {
    setActiveTab('knowledgebase');
  };

  const handleNavigateToEditor = () => {
    //setActiveTab('editor');
  };

  const handleSelectExecution = async (executionId: string, workpillId: string | null, executionName?: string) => {
    // 🛡️ Check if agent is streaming
    if (chatState.isStreaming) {
      setPendingWarningAction(() => () => proceedWithExecutionSelection(executionId, workpillId, executionName));
      setWarningType('execution_change');
      setShowWarningModal(true);
      return;
    }

    await proceedWithExecutionSelection(executionId, workpillId, executionName);
  };

  const proceedWithExecutionSelection = async (executionId: string, workpillId: string | null, executionName?: string) => {
    const currentConfig = configService.getConfig();
    
    // If there's no previous execution or it's the same, don't show modal
    if (!currentConfig.executionId || currentConfig.executionId === executionId) {
      await performExecutionChange(executionId, workpillId, executionName);
      return;
    }
    
    // There's a different previous execution, show confirmation modal
    setPendingExecution({ executionId, workpillId, executionName });
    setShowChangeModal(true);
  };

  const handleConfirmExecutionChangeSave = async () => {
    if (!pendingExecution) return;
    
    setIsChangingExecution(true);
    try {
      await performExecutionChange(
        pendingExecution.executionId,
        pendingExecution.workpillId,
        pendingExecution.executionName,
        true // Save changes
      );
      setShowChangeModal(false);
      setPendingExecution(null);
    } catch (e) {
      if (!(e instanceof SaveConflictUserResolutionError)) {
        throw e;
      }
    } finally {
      setIsChangingExecution(false);
    }
  };

  const handleConfirmExecutionChangeDiscard = async () => {
    if (!pendingExecution) return;
    
    setIsChangingExecution(true);
    await performExecutionChange(
      pendingExecution.executionId,
      pendingExecution.workpillId,
      pendingExecution.executionName,
      false // Don't save changes
    );
    setIsChangingExecution(false);
    setShowChangeModal(false);
    setPendingExecution(null);
  };

  const handleCancelExecutionChange = () => {
    setShowChangeModal(false);
    setPendingExecution(null);
  };

  const performExecutionChange = async (
    executionId: string,
    workpillId: string | null,
    executionName?: string,
    shouldSave: boolean = true
  ) => {
    try {
      console.log('🔄 ========================================');
      console.log('🔄 Changing execution...');
      
      // 1. Optionally save current document before changing
      const currentConfig = configService.getConfig();
      
      if (currentConfig.executionId && shouldSave) {
        console.log('💾 Saving current document before changing...');
        try {
          await StorageService.saveDocumentToSupabase({ throwOnConflictUserChoice: true });
        } catch (saveError) {
          if (saveError instanceof SaveConflictUserResolutionError) {
            console.log('ℹ️ Execution change aborted: save conflict resolved without upload');
            throw saveError;
          }
          console.error('⚠️ Error saving current document (continuing anyway):', saveError);
        }
      }
      
      // 2. Update config service
      configService.initialize({
        executionId,
        workpillId: workpillId ?? null,
      });
      
      // 3. Update local state
      setSelectedExecutionId(executionId);
      // No need to hide executions list anymore
      setCurrentExecutionName(executionName);
      
      // 4. Load template for new execution
      const loadResult = await StorageService.loadTemplateForExecution(workpillId ?? '', executionId);
      
      if (!loadResult.success) {
        console.error('⚠️ Error loading template:', loadResult.error);
      }

      console.log('✅ Execution change completed');
      
    } catch (error) {
      if (error instanceof SaveConflictUserResolutionError) {
        throw error;
      }
      console.error('💥 Error in performExecutionChange:', error);
      setSelectedExecutionId(executionId);
      setCurrentExecutionName(executionName);
      configService.initialize({ executionId, workpillId: workpillId ?? null });
    }
  };

  const handleLoginSuccess = (userId: string) => {
    console.log('🎉 Login successful, user ID:', userId);
    configService.setUserId(userId);
    setIsAuthenticated(true);
  };

  // Compute hasSelectedProject early so it can be used in handleLogout
  const hasSelectedProject = !!(selectedExecutionId || appConfig.executionId);

  const handleLogout = async () => {
    // 🛡️ Check if agent is streaming
    if (chatState.isStreaming) {
      setPendingWarningAction(() => () => setShowLogoutModal(true));
      setWarningType('logout');
      setShowWarningModal(true);
      return;
    }

    // If no project selected, logout directly without asking to save
    if (!hasSelectedProject) {
      await proceedWithLogout();
      return;
    }

    setShowLogoutModal(true);
  };

  const handleConfirmLogoutSave = async () => {
    setIsLoggingOut(true);
    try {
      // Save document before logging out
      console.log('💾 Saving document before logout...');
      await StorageService.saveDocumentToSupabase({ throwOnConflictUserChoice: true });
    } catch (error) {
      if (error instanceof SaveConflictUserResolutionError) {
        setIsLoggingOut(false);
        return;
      }
      console.error('Error saving during logout:', error);
    }
    
    await proceedWithLogout();
    setIsLoggingOut(false);
    setShowLogoutModal(false);
  };

  const handleConfirmLogoutDiscard = async () => {
    setShowLogoutModal(false);
    await proceedWithLogout();
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const resolveSaveConflict = (choice: SaveConflictChoice) => {
    const resolve = saveConflictResolverRef.current;
    saveConflictResolverRef.current = null;
    setSaveConflictPrompt(null);
    resolve?.(choice);
  };

  const proceedWithLogout = async () => {
    try {
      await authService.signOut();
      setSelectedExecutionId(undefined);
      setActiveTab('chat'); // Always go to chat (which will show blocked state)
      setIsSettingsOpen(false); // Close settings on logout
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Warning Modal Handlers
  const handleWarningConfirm = async () => {
    setShowWarningModal(false);
    await pendingWarningAction();
  };

  const handleWarningCancel = () => {
    setShowWarningModal(false);
    setPendingWarningAction(() => {});
    setWarningType(null);
  };

  const handleMapToWord = (field: ExtractedDataField) => {
    console.log('Mapping field to Word:', field);
    alert(`Mapping field "${field.name}" with value "${field.value}" to Word document.`);
  };

  // Show spinner while checking authentication
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 gap-4 text-muted-foreground">
        <Spinner size="lg" />
        <p style={{ marginTop: '16px', color: '#64748b' }}>
          Verifying authentication...
        </p>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AuthFlow onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      <AppHeader 
        // Navigation props
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onOpenSettings={() => setIsSettingsOpen(true)}
        
        // Project Selector props
        onSelectExecution={handleSelectExecution}
        selectedExecutionId={selectedExecutionId || appConfig.executionId}
        isProjectSelectorOpen={isProjectSelectorOpen}
        onProjectSelectorOpenChange={setIsProjectSelectorOpen}
        
        // Project state
        hasSelectedProject={hasSelectedProject}
        
        // Agent mode
        isAgentMode={chatState.isAgentMode}
        onToggleAgentMode={chatState.toggleAgentMode}
      />
      
      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onLogout={handleLogout}
        user={{
          email: currentUser?.email,
          name: currentUser?.user_metadata?.full_name || currentUser?.email?.split('@')[0] || 'User'
        }}
      />
      
      {/*
        Persistent chat: stays mounted under other panels. Absolute layers avoid flex height
        collapse (display:none + height:0) when switching tabs.
      */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            "absolute inset-0 z-10 flex min-h-0 flex-col overflow-hidden transition-opacity duration-150 ease-out",
            activeTab === "chat"
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0"
          )}
          aria-hidden={activeTab !== "chat"}
        >
          {hasSelectedProject ? (
            <ChatPanel
              workpillId={appConfig.workpillId}
              workpillExecutionId={selectedExecutionId || appConfig.executionId}
              userId={appConfig.userId}
              isDevelopment={appConfig.isDevelopment}
              chatState={chatState}
            />
          ) : (
            <ChatBlockedState onOpenSelector={() => setIsProjectSelectorOpen(true)} />
          )}
        </div>

        {activeTab === "documents" && (
          <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
            <DataPanel
              onMapToWord={handleMapToWord}
              executionId={selectedExecutionId || appConfig.executionId}
              userId={appConfig.userId}
            />
          </div>
        )}

        {activeTab === "knowledgebase" && (
          <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
            <KnowledgeBasePanel
              executionId={selectedExecutionId || appConfig.executionId || undefined}
              workpillId={appConfig.workpillId || undefined}
              onOpenProjectSelector={() => setIsProjectSelectorOpen(true)}
            />
          </div>
        )}

        {activeTab === "blocks" && (
          <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
            <BlocksPanel
              executionId={selectedExecutionId || appConfig.executionId}
              workpillId={appConfig.workpillId}
              userId={appConfig.userId}
              onOpenProjectSelector={() => setIsProjectSelectorOpen(true)}
            />
          </div>
        )}

        {activeTab === "integrations" && !configService.isB2C() && (
          <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
            <IntegrationsPanel
              executionId={selectedExecutionId || appConfig.executionId}
              workpillId={appConfig.workpillId}
            />
          </div>
        )}

        {activeTab === "data" && (
          <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
            <ProjectVariablesPanel
              executionId={selectedExecutionId || appConfig.executionId}
              onOpenProjectSelector={() => setIsProjectSelectorOpen(true)}
            />
          </div>
        )}

        {activeTab === "templates" && (
          <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
            <ExecutionTemplatesPanel
              executionId={selectedExecutionId || appConfig.executionId}
              onOpenProjectSelector={() => setIsProjectSelectorOpen(true)}
            />
          </div>
        )}
      </div>
      
      {/* Confirmation modal for execution change */}
      <ExecutionChangeModal
        isOpen={showChangeModal}
        onConfirmSave={handleConfirmExecutionChangeSave}
        onConfirmDiscard={handleConfirmExecutionChangeDiscard}
        onCancel={handleCancelExecutionChange}
        currentExecutionName={currentExecutionName}
        newExecutionName={pendingExecution?.executionName}
        isSaving={isChangingExecution}
      />

      {/* Confirmation modal for logout */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onConfirmSave={handleConfirmLogoutSave}
        onConfirmDiscard={handleConfirmLogoutDiscard}
        onCancel={handleCancelLogout}
        isSaving={isLoggingOut}
      />

      <SaveConflictModal
        isOpen={saveConflictPrompt !== null}
        lastKnownFileName={saveConflictPrompt?.lastKnownFileName ?? ''}
        remoteFileName={saveConflictPrompt?.remoteFileName ?? ''}
        variant={saveConflictPrompt?.variant ?? 'remote-newer'}
        onChoice={resolveSaveConflict}
      />

      {/* Warning Modal for Interruption */}
      <WarningModal 
        isOpen={showWarningModal}
        title={warningType === 'execution_change' ? "Change execution" : "Sign out"}
        message="The agent is currently working. If you change execution or sign out, the current process will be interrupted."
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
        confirmLabel="Interrupt and continue"
        cancelLabel="Go back"
      />
    </div>
  );
};

export default MainApplication;
